import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { TenantContext } from '../../tenant/TenantContext';
import { SecurityContext } from 'security/SecurityContext';
import { FormField } from '../../uiwidgets/FormField';
import { FormContainer } from '../../uiwidgets/FormContainer';
import { SubmitButton } from '../../uiwidgets/SubmitButton';
import { CancelButton } from 'uiwidgets/CancelButton';
import { Row, Col, Card, Table, Button, Modal } from 'react-bootstrap';
import { API } from '../../api/API';
import { ADMIN_PAYMENT_CARDS, PAYMENT_CARDS, PROFILE_URL } from '../../config/config';
import { extractError } from '../../api/ErrorUtil';
import {
    validAddress, containsAlphaNumAndSpaceOnly, isEmailAddressValid,
    isPhoneValid, isPostalCodeValid, containsAlphaNumAndSpaceAndPunctuationOnly
} from 'util/Validators';
import _ from "lodash";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import CountrySelect from 'uiwidgets/CountrySelect';
import ProvinceSelect from 'uiwidgets/ProvinceSelect';
import PostalCode from 'uiwidgets/PostalCode';
import { openLightbox } from 'util/ElavonLightBox';
import AppSpinner from 'uiwidgets/AppSpinner';
import useExternalScripts from 'util/useExternalScripts';
import { PAGE_SIZE } from '../../config/config';
import { NewButton } from 'uiwidgets/NewButton';
import StripeElements from 'util/StripeElements';

const PaymentCards = () => {
    const initialState = {
        paymentCardId: "",
        expDate: "",
        email: "",
        firstName: "",
        lastName: "",
        address: "",
        city: "",
        state: "ON",
        zip: "",
        country: "CA",
        mobilePhoneNumber: "",
        errors: {},
        infoMessage: "",
        editMode: false,
        processing: false,
        refresh: 0,

    }
    const initialValidationState = {
        expDateError: false,
        emailError: false,
        firstNameError: false,
        lastNameError: false,
        addressError: false,
        cityError: false,
        stateError: false,
        zipError: false,
        countryError: false,
        mobilePhoneNumberError: false,

        expDateErrMsg: "",
        emailErrMsg: "",
        firstNameErrMsg: "",
        lastNameErrMsg: "",
        addressErrMsg: "",
        cityErrMsg: "",
        stateErrMsg: "",
        zipErrMsg: "",
        countryErrMsg: "",
        mobilePhoneNumberErrMsg: "",
    }

    const messageReset = () => {
        return {
            infoMessage: "",
            errors: {}
        }
    }

    const onToastClose = () => {
        setFormData({
            ...formData,
            ...messageReset()
        });
    }

    const { businessConfig } = useContext(TenantContext);
    const secCtx = useContext(SecurityContext);
    const paymentGateway = businessConfig.paymentGateway;

    useExternalScripts(businessConfig.elavonLightboxUrl);

    const [paymentCards, setPaymentCards] = useState([]);
    const [formData, setFormData] = useState(initialState);
    const [formValidityData, setFormValidityData] = useState(initialValidationState);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalNumberOfPages, setTotalNumberOfPages] = useState(0);
    const [keyword, setKeyword] = useState("");
    const [keywordValidityData, setKeywordValidityData] = useState({ keywordErrMsg: "", keywordError: false });
    const [readOnly, setReadOnly] = useState(true);
    const saveCardFunction = useRef(null);


    const isFormReadOnly = () => {
        if (formData.editMode) {
            return false;
        }
        if (secCtx.profile.roles.includes("ROLE_CONSUMER")) {
            return false
        }
        return true;
    }

    const fetchPaymentCards = useCallback(async () => {
        let paymentCards = [];
        let totalNumberOfPages = 1;

        try {
            if (secCtx.profile.roles.includes("ROLE_CONSUMER")) {
                let paymentCardsResponse = await API.get(PAYMENT_CARDS);
                paymentCards = paymentCardsResponse.data;
            } else if (secCtx.profile.roles.includes("ROLE_BUSINESS_ADMIN")) {
                let paymentCardsResponse = await API.get(ADMIN_PAYMENT_CARDS, {
                    params: {
                        size: PAGE_SIZE,
                        page: currentPage,
                        keyword: keyword.trim(),
                    }
                });
                paymentCards = paymentCardsResponse.data.content;
                totalNumberOfPages = paymentCardsResponse.data.totalPages;
            }

            setPaymentCards(paymentCards);
            setTotalNumberOfPages(totalNumberOfPages);
            setReadOnly(isFormReadOnly());

        } catch (error) {
            let { message, errors, httpStatus } = extractError(error);
            if (httpStatus === 404) {
                setPaymentCards([]);
                setFormData({ ...formData, infoMessage: "No Payment Cards were found" });
            } else {
                setFormData({ ...formData, errors: { message, errors, httpStatus } });
            }
        }

    })

    useEffect(() => {
        setReadOnly(isFormReadOnly())
    }, [formData.editMode]);

    useEffect(() => {
        fetchPaymentCards();
    }, []);


    useEffect(() => {
        if (formData.refresh) {
            fetchPaymentCards();
        }
    }, [formData.refresh]);


    const refreshPage = async () => {
        setFormData({
            ...formData,
            refresh: Math.random()
        })
    }
    const handlePaging = async (page) => {
        if (page < 0) {
            alert("Reached the beginning of data set")
            page = 0;
        }
        if (page > totalNumberOfPages - 1) {
            alert("Reached the end of data set")
            page = totalNumberOfPages - 1;
        }
        setCurrentPage(page);
        refreshPage();
    }

    const handleFilterKeyDown = async (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            refreshPage();
        }
    };
    const clearFilter = async (event) => {
        setKeyword("");
        setCurrentPage(0);
        refreshPage();
    };

    const handleKeywordChange = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setKeyword(value);
    }

    const handleKeywordValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setKeywordValidityData({
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })
    }

    const editPaymentCard = (event, paymentCard) => {
        setFormData({
            paymentCardId: paymentCard.paymentCardId,
            email: paymentCard.email,
            firstName: paymentCard.firstName,
            lastName: paymentCard.lastName,
            address: paymentCard.address,
            city: paymentCard.city,
            state: paymentCard.state,
            zip: paymentCard.zip,
            country: paymentCard.country,
            mobilePhoneNumber: paymentCard.mobilePhoneNumber,
            maskedAccountNumber: paymentCard.maskedAccountNumber,
            editMode: true,
            errors: {},
            infoMessage: "",
        });

    }

    const handleCancel = async (event) => {
        setFormData(initialState);
        setFormValidityData(initialValidationState);
    }

    const handleFormDataChange = async (event) => {
        const name = event.target.name;
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData({
            ...formData,
            [name]: value
        });
    }

    const handleValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setFormValidityData({
            ...formValidityData,
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })
    }

    const handleCountryChange = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let defaultState = "ON";
        if (value === "US") {
            defaultState = "MI";
        }

        if (value === "CA") {
            defaultState = "ON";
        }
        setFormData({
            ...formData,
            [name]: value,
            state: defaultState
        });
    }

    const validateInput = (name, value) => {
        let hasError = false,
            error = ""
        switch (name) {
            case "firstName":
                if (value.trim() === "") {
                    hasError = true
                    error = "First name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid First Name. Only alphbest are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "lastName":
                if (value.trim() === "") {
                    hasError = true
                    error = "Last name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid Last Name. Only alphbest are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "address":
                if (value.trim() === "") {
                    hasError = true
                    error = "Address name cannot be empty"
                } else if (!validAddress(value)) {
                    hasError = true
                    error = "Invalid Address. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "city":
                if (value.trim() === "") {
                    hasError = true
                    error = "City name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid First Name. Only alphbest are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "state":
                if (value.trim() === "") {
                    hasError = true
                    error = "State name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid State. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "country":
                if (value.trim() === "") {
                    hasError = true
                    error = "Country name cannot be empty"
                } else if (!containsAlphaNumAndSpaceOnly(value)) {
                    hasError = true
                    error = "Invalid Country. Avoid Special characters"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "email":
                if (value.trim() === "") {
                    hasError = true
                    error = "Email cannot be empty"
                } else if (!isEmailAddressValid(value)) {
                    hasError = true
                    error = "Invalid Email"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "mobilePhoneNumber":
                if (value.trim() === "") {
                    hasError = true
                    error = "Mobile cannot be empty"
                } else if (!isPhoneValid(value)) {
                    hasError = true
                    error = "Invalid Mobile Number. Use 10 digits only"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "zip":
                if (value.trim() === "") {
                    hasError = true
                    error = "Postal Code cannot be empty"
                } else if (!isPostalCodeValid(value, formData.country)) {
                    hasError = true
                    error = "Invalid Postal Code"
                } else {
                    hasError = false
                    error = ""
                }
                break
            case "keyword":
                if (value.trim() === "") {
                    hasError = false
                    error = ""
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Invalid keyword. Only alphanumeric characters are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            default:
                break
        }
        return { hasError, error }
    }

    const handlePaymentResult = async ({ paymentStatus, errors, response, cardTokenId }) => {
        if (paymentStatus !== 'SUCCEEDED') {
            setFormData({
                ...formData,
                errors: errors,
                processing: false
            })
            return false;
        }

        const paymentCard = {
            gwCardTokenResponse: response,
            paymentCardId: formData.paymentCardId,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            country: formData.country,
            mobilePhoneNumber: formData.mobilePhoneNumber
        }
        try {
            let paymentPutCardResponse = {};
            if (secCtx.profile.roles.includes("ROLE_CONSUMER")) {
                if (_.isEmpty(formData.paymentCardId)) {
                    paymentPutCardResponse = await API.post(PAYMENT_CARDS, paymentCard);
                } else {
                    paymentPutCardResponse = await API.put(PAYMENT_CARDS + "/" + formData.paymentCardId, paymentCard);
                }
            } else if (secCtx.profile.roles.includes("ROLE_BUSINESS_ADMIN")) {
                paymentPutCardResponse = await API.put(ADMIN_PAYMENT_CARDS + "/" + formData.paymentCardId, paymentCard);
            }
            setFormData({
                ...initialState,
                refresh: Math.random(),
                infoMessage: 'Credit Card was updated successfully'
            });
        } catch (error) {
            let errors = extractError(error);
            setFormData({
                ...formData,
                processing: false,
                errors: errors
            });
            return false;
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        let isFormValid = true;
        const tagNames = ['select', 'input', 'textarea'];
        console.log(formData);

        for (let tagName of tagNames) {
            //validate full form
            for (let i = 0; i < event.target.getElementsByTagName(tagName).length; i++) {
                let fieldName = event.target.getElementsByTagName(tagName)[i].name;
                let fieldValue = event.target.getElementsByTagName(tagName)[i].value;
                let { hasError, error } = validateInput(fieldName, fieldValue);
                setFormValidityData({
                    ...formValidityData,
                    [fieldName + 'Error']: hasError,
                    [fieldName + 'ErrMsg']: error
                })
                if (hasError) {
                    isFormValid = false;
                    alert("Form has validation error")
                    return false;
                }
            }
        }

        if (!isFormValid) {
            alert("The form still has validation errors!");
            return false;
        }
        setFormData({ ...formData, readOnly: true, processing: true });
        //if every thing works open light box to collect card data

        if (paymentGateway === 'ELAVON') {
            openLightbox({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                mobilePhoneNumber: formData.mobilePhoneNumber,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                zip: formData.zip
            }, handlePaymentResult);
        } else if (paymentGateway === 'STRIPE') {
            const status = await saveCardFunction.current();
        }


    }

    const addNewCard = async () => {
        if (secCtx.profile.roles.includes("ROLE_BUSINESS_ADMIN")) {
            return;
        }
        //fetch profile.
        try {
            let profileResp = await API.get(PROFILE_URL);
            let profile = profileResp.data;
            setFormData({
                email: profile.email,
                firstName: profile.firstName,
                lastName: profile.lastName,
                address: profile.address,
                city: profile.city,
                state: profile.state,
                zip: profile.zip,
                country: profile.country,
                mobilePhoneNumber: profile.mobilePhoneNumber,
                editMode: true,
                errors: {},
                infoMessage: "",
            });

        } catch (err) {
            let errors = extractError(err);
            if (errors.httpStatus !== 404) {
                setFormData({ ...formData, errors: errors });
                return;
            }
        };
    }
    return (
        <>
            {formData.processing && <AppSpinner style={{ marginBottom: 27 }} animation="border" variant="success" />}
            <FormContainer title="Payment Cards" submitButtonFunction={handleSubmit}
                errors={formData.errors} infoMessage={formData.infoMessage} toastCloseFunc={onToastClose}>
                {secCtx.profile.roles.includes("ROLE_CONSUMER") &&
                    <Row>
                        <Col>

                            <NewButton label="Add New Card" onClick={addNewCard} /><br />
                            <small>To add a new card, click on the "Add New Card" button above. Fill in the billing address and other details below.
                                and click on "Click to Add Card Details and Save". This will display a dialog box to enter card details. </small>
                        </Col>
                    </Row>}
                {formData.editMode && <>
                    <Row>
                        <Col mb={6} lg={6} sm={12}>
                            <FormField name="firstName" label="First Name" type="text" value={formData.firstName}
                                placeholder="First Name" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.firstNameError} valMsg={formValidityData.firstNameErrMsg}
                                readOnly={readOnly} />
                        </Col>
                        <Col mb={6} lg={6} sm={12}>
                            <FormField name="lastName" label="Last Name" type="text" value={formData.lastName}
                                placeholder="Last Name" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.lastNameError} valMsg={formValidityData.lastNameErrMsg}
                                readOnly={readOnly} />
                        </Col>
                    </Row>
                    <Row>
                        <Col mb={6} lg={6} sm={12}>
                            <FormField name="email" label="Email" type="text" value={formData.email}
                                placeholder="Email" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.emailError} valMsg={formValidityData.emailErrMsg}
                                readOnly={true} />
                        </Col>

                        <Col mb={6} lg={6} sm={12}>
                            <FormField name="mobilePhoneNumber" label="Mobile Phone Number" type="text" value={formData.mobilePhoneNumber}
                                placeholder="Mobile Phone Number" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.mobilePhoneNumberError} valMsg={formValidityData.mobilePhoneNumberErrMsg}
                                readOnly={readOnly} />
                        </Col>
                    </Row>
                    {formData.paymentCardId != null &&
                        <Row>
                            <Col mb={6} lg={6} sm={12}>
                                <FormField name="maskedCardNumber" label="Card Number" type="text" value={formData.maskedAccountNumber}
                                    placeholder="Card Number" readOnly={true} valstate={true} />
                            </Col>
                        </Row>}
                    <Row>
                        <Col mb={12} lg={12} sm={12}>
                            <FormField name="address" label="Address" type="text" value={formData.address}
                                placeholder="Address" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.addressError} valMsg={formValidityData.addressErrMsg}
                                readOnly={readOnly} />
                        </Col>
                    </Row>
                    <Row>
                        <Col mb={2} lg={2} sm={6}>
                            <CountrySelect name="country" onChange={handleCountryChange} onBlur={handleValidation}
                                valstate={!formValidityData.countryError} valMsg={formValidityData.countryErrMsg}
                                readOnly={readOnly} value={formData.country} />
                        </Col>
                        <Col mb={4} lg={4} sm={12}>
                            <FormField name="city" label="City" type="text" value={formData.city}
                                placeholder="City" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.cityError} valMsg={formValidityData.cityErrMsg}
                                readOnly={readOnly} />
                        </Col>
                        <Col mb={2} lg={2} sm={6}>
                            <ProvinceSelect name="state" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.stateError} valMsg={formValidityData.stateErrMsg}
                                readOnly={readOnly}
                                value={formData.state} country={formData.country} />
                        </Col>
                        <Col mb={4} lg={4} sm={6}>
                            <PostalCode name="zip" label="Postal Code" type="text" value={formData.zip}
                                placeholder="Postal Code" onChange={handleFormDataChange} onBlur={handleValidation}
                                valstate={!formValidityData.zipError} valMsg={formValidityData.zipErrMsg}
                                readOnly={readOnly}
                                country={formData.country} />
                        </Col>
                    </Row>
                    {paymentGateway && paymentGateway === 'STRIPE' && <>
                        <Row><Col>&nbsp;</Col></Row>
                        <Row>
                            <Col>
                                <StripeElements formData={formData} saveCardFunction={saveCardFunction} handlePaymentResult={handlePaymentResult} />
                            </Col>
                        </Row>
                    </>}
                    <Row><Col>

                        <SubmitButton label={"Click to " + (formData.paymentCardId == null ? "Add" : "Update") + " Card details and Save "} disabled={readOnly} className="me-4" />
                        <CancelButton label='Cancel' disabled={readOnly} onClick={handleCancel} />
                    </Col>
                    </Row>
                    <Row><Col>&nbsp;</Col></Row>
                    <Row><Col>&nbsp;</Col></Row>
                </>}
                {secCtx.profile.roles.includes("ROLE_BUSINESS_ADMIN") && <>
                    <Row>
                        <Col sm={12} md={4} lg={4}>
                            <FormField name="keyword" label="Keyword" type="text" value={keyword}
                                placeholder="email, account number" onChange={handleKeywordChange}
                                onBlur={handleKeywordValidation} onKeyDown={handleFilterKeyDown}
                                valstate={!keywordValidityData.keywordError} valMsg={keywordValidityData.keywordErrMsg} />
                        </Col>

                        <Col sm={6} md={2} lg={2}>
                            <br />
                            <Button variant="info" className="border-0 btn-fill" onClick={clearFilter}><strong><small>Clear Filter</small></strong></Button>
                        </Col>
                    </Row>


                    <Row>
                        <Col className='d-flex justify-content-end'>
                            <Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(currentPage - 1) }}>
                                <h6><FontAwesomeIcon icon={faArrowLeft} onClick={(event) => { handlePaging(currentPage - 1) }} />Previous Page</h6>
                            </Button>
                            &nbsp;<span className="border-0 text-dark btn"><h6>{currentPage + 1} of {totalNumberOfPages}</h6></span>
                            &nbsp;<Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(currentPage + 1) }}>
                                <h6>Next Page<FontAwesomeIcon icon={faArrowRight} /></h6>
                            </Button>
                        </Col>

                    </Row></>}

                <Table className="table-hover table-striped">
                    <thead>
                        <tr>
                            <th className="border-0">Card Number</th>
                            <th className="border-0">Exp Date</th>
                            <th className="border-0">email</th>
                            <th className="border-0">First Name</th>
                            <th className="border-0">Address</th>
                            <th className="border-0">Update</th>
                        </tr>

                    </thead>
                    <tbody>
                        {paymentCards && !_.isEmpty(paymentCards) && paymentCards.map((paymentCard, idx) => (
                            <tr key={idx}>
                                <td>{paymentCard.maskedAccountNumber}</td>
                                <td>{paymentCard.expDate}</td>
                                <td>{paymentCard.email}</td>
                                <td>{paymentCard.firstName} {paymentCard.lastName}</td>
                                <td>{paymentCard.address},{paymentCard.city}, {paymentCard.state}, {paymentCard.zip}, {paymentCard.country}</td>
                                <td><Button variant="dark" className="border-0 text-dark" onClick={evt => editPaymentCard(evt, paymentCard)}
                                    disabled={false}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button></td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </FormContainer>
        </>
    )
}

export default PaymentCards